import { UserSettings } from '@protos/user';
import { useUserSettingsContext } from '@shared/contexts/UserContextProvider';
import { Dashboard } from '@shared/protos/v2/dashboard';
import { useCallback, useMemo } from 'react';

export interface SavedLayout {
  id: string;
  name: string;
  dashboard: Dashboard;
  timestamp: number;
}

export const MAX_SAVED_LAYOUTS = 10;

const transformLayoutsWithDashboardId = (layouts: any, dashboardId: number) => {
  if (!layouts) return layouts;

  const transformedLayouts: any = {};

  Object.keys(layouts).forEach(breakpoint => {
    const breakpointLayouts = layouts[breakpoint];
    if (Array.isArray(breakpointLayouts)) {
      transformedLayouts[breakpoint] = breakpointLayouts.map(layoutItem => ({
        ...layoutItem,
        i: `${layoutItem.i}_${dashboardId}`,
      }));
    }
  });

  return transformedLayouts;
};

const removeDashboardIdFromLayouts = (layouts: any) => {
  if (!layouts) return layouts;

  const cleanedLayouts: any = {};

  Object.keys(layouts).forEach(breakpoint => {
    const breakpointLayouts = layouts[breakpoint];
    if (Array.isArray(breakpointLayouts)) {
      cleanedLayouts[breakpoint] = breakpointLayouts.map(layoutItem => ({
        ...layoutItem,
        i: layoutItem.i.split('_').slice(0, -1).join('_'), // Remove the dashboard ID suffix
      }));
    }
  });

  return cleanedLayouts;
};

export const useLayoutStorage = () => {
  const userSettings = useUserSettingsContext()?.userSettings;
  const updateUserSettings = useUserSettingsContext()?.updateUserSettings;

  const layouts = useMemo(() => {
    return userSettings?.dashboard?.savedLayouts || [];
  }, [userSettings?.dashboard?.savedLayouts]);

  const getSavedLayoutLength = useCallback(() => {
    return layouts.length;
  }, [layouts]);

  const saveLayoutsToStorage = useCallback(
    (newLayouts: SavedLayout[]) => {
      try {
        if (!updateUserSettings) {
          throw new Error('No update function provided');
        }

        const newSettings: UserSettings = {
          ...userSettings!,
          dashboard: {
            ...userSettings!.dashboard,
            savedLayouts: newLayouts,
          },
        };

        const updateResult = updateUserSettings(newSettings);

        updateResult
          .then(() => {
            console.log('Layouts saved successfully to server');
          })
          .catch(error => {
            console.error('Failed to save layouts to server:', error);
          });
      } catch (error) {
        console.error('Error saving layouts to user settings:', error);
      }
    },
    [userSettings, updateUserSettings]
  );

  const saveLayout = useCallback(
    (dashboard: Dashboard, layoutName?: string): SavedLayout => {
      const hasTransformedIds = Object.values(dashboard.json.layouts || {}).some(
        breakpointLayouts =>
          Array.isArray(breakpointLayouts) &&
          breakpointLayouts.some(layout => layout.i && layout.i.includes('_') && !isNaN(parseInt(layout.i.split('_').pop() || '')))
      );

      const cleanedDashboardJson = {
        ...dashboard.json,
        layouts: hasTransformedIds ? removeDashboardIdFromLayouts(dashboard.json.layouts) : dashboard.json.layouts,
      };

      const completeDashboard: Dashboard = {
        id: dashboard.id,
        name: dashboard.name,
        json: cleanedDashboardJson,
        last_modified: dashboard.last_modified,
        websocket_uid: dashboard.websocket_uid,
        user_id: dashboard.user_id,
      };

      const newLayout: SavedLayout = {
        id: `${dashboard.id}_${Date.now()}`,
        name: layoutName || `${dashboard.name} Layout`,
        dashboard: completeDashboard,
        timestamp: Date.now(),
      };

      const updatedLayouts = [newLayout, ...layouts].slice(0, MAX_SAVED_LAYOUTS);
      saveLayoutsToStorage(updatedLayouts);
      return newLayout;
    },
    [layouts, saveLayoutsToStorage]
  );

  const getSavedLayouts = useCallback(() => {
    return layouts;
  }, [layouts]);

  const getSavedLayoutsForDashboard = useCallback(
    (dashboardId: number) => {
      return layouts.filter(layout => layout.dashboard?.id === dashboardId);
    },
    [layouts]
  );

  const getSavedLayoutsGroupedByDashboard = useCallback(() => {
    const grouped: Record<string, SavedLayout[]> = {};

    layouts.forEach(layout => {
      const key = `${layout.dashboard?.name} (ID: ${layout.dashboard?.id})`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(layout);
    });

    return grouped;
  }, [layouts]);

  const loadLayout = useCallback(
    (layoutId: string, targetDashboardId?: number) => {
      const layout = layouts.find(layout => layout.id === layoutId);
      if (!layout) return null;

      if (targetDashboardId && layout.dashboard?.json?.layouts) {
        const transformedDashboard = {
          ...layout.dashboard,
          id: targetDashboardId,
          json: {
            ...layout.dashboard.json,
            layouts: transformLayoutsWithDashboardId(layout.dashboard.json.layouts, targetDashboardId),
          },
        };

        return {
          ...layout,
          dashboard: transformedDashboard,
        };
      }

      return layout;
    },
    [layouts]
  );

  const deleteLayout = useCallback(
    (layoutId: string) => {
      const filteredLayouts = layouts.filter(layout => layout.id !== layoutId);
      saveLayoutsToStorage(filteredLayouts);
    },
    [layouts, saveLayoutsToStorage]
  );

  const clearAllLayouts = useCallback(() => {
    saveLayoutsToStorage([]);
  }, [saveLayoutsToStorage]);

  return {
    layouts,
    getSavedLayoutLength,
    saveLayout,
    getSavedLayouts,
    getSavedLayoutsForDashboard,
    getSavedLayoutsGroupedByDashboard,
    loadLayout,
    deleteLayout,
    clearAllLayouts,
    transformLayoutsWithDashboardId,
  };
};
